import React, { useState, useEffect } from 'react';
import { reportingAPI } from '../services/api';
import toast from 'react-hot-toast';
import ReportViewer from './ReportViewer';
import { useAuth } from '../context/AuthContext';
import AiDisclaimer from './AiDisclaimer';
import { formatAxiosError } from '../utils/apiError';

const ReportList = ({ candidateId, reportType = null, showGenerateButton = false, onGenerateReport = null, showHeader = true }) => {
  const { user } = useAuth();
  const isAdmin = user?.userType === 'admin';
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    if (!candidateId) {
      setLoading(false);
      return;
    }
    fetchReports();
  }, [candidateId, reportType, filter, sortBy]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportingAPI.getCandidateReports(candidateId, reportType, 50);
      let filteredReports = Array.isArray(response.data) ? response.data : [];

      // Apply filter
      if (filter !== 'all') {
        filteredReports = filteredReports.filter(report => report.report_type === filter);
      }

      // Apply sorting
      filteredReports.sort((a, b) => {
        switch (sortBy) {
          case 'newest':
            return new Date(b.generated_at || 0) - new Date(a.generated_at || 0);
          case 'oldest':
            return new Date(a.generated_at || 0) - new Date(b.generated_at || 0);
          case 'type':
            return a.report_type.localeCompare(b.report_type);
          case 'status':
            return a.status.localeCompare(b.status);
          default:
            return 0;
        }
      });

      setReports(filteredReports);
    } catch (err) {
      console.error('Error fetching reports:', err);
      const message = formatAxiosError(err, 'Failed to load reports');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (reportId) => {
    try {
      const response = await reportingAPI.downloadReportPDF(reportId);
      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded successfully');
    } catch (err) {
      console.error('Error downloading report:', err);
      toast.error('Failed to download report');
    }
  };

  const handleToggleVisibility = async (reportId, report) => {
    try {
      // Get current status - check for candidate_can_view first, then fallback to is_public
      // Default to true (active) if neither field exists
      const currentStatus = report.candidate_can_view !== undefined 
        ? report.candidate_can_view 
        : (report.is_public !== undefined ? report.is_public : true);
      
      const newStatus = !currentStatus;
      await reportingAPI.toggleReportVisibility(reportId, newStatus);
      toast.success(`Report is now ${newStatus ? 'active' : 'inactive'}. ${newStatus ? 'Candidate can view this report' : 'Candidate cannot view this report'}`);
      fetchReports();
    } catch (err) {
      console.error('Error toggling report visibility:', err);
      toast.error('Failed to update report visibility');
    }
  };

  const getReportVisibilityStatus = (report) => {
    // Check if candidate_can_view exists, otherwise use is_public
    // Default to true (active) if neither exists
    if (report.candidate_can_view !== undefined) {
      return report.candidate_can_view;
    }
    if (report.is_public !== undefined) {
      return report.is_public;
    }
    return true; // Default to active
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'generating': return 'text-yellow-600 bg-yellow-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getReportTypeColor = (type) => {
    switch (type) {
      case 'assessment': return 'text-blue-600 bg-blue-100';
      case 'prescreening': return 'text-purple-600 bg-purple-100';
      case 'impression': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getReportTypeIcon = (type) => {
    switch (type) {
      case 'assessment': return '📊';
      case 'prescreening': return '❓';
      case 'impression': return '🎯';
      default: return '📄';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Reports</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchReports}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls - Only show if showHeader prop is true */}
      {showHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Reports</h2>
            <p className="text-gray-600">View and manage generated reports</p>
          </div>
          
          {showGenerateButton && onGenerateReport && (
            <button
              onClick={onGenerateReport}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Generate Report
            </button>
          )}
        </div>
      )}

      <AiDisclaimer variant="assessment" />

      {/* Filters and Sorting */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Types</option>
            <option value="assessment">Assessment</option>
            <option value="prescreening">Prescreening</option>
            <option value="impression">Impression</option>
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="type">By Type</option>
            <option value="status">By Status</option>
          </select>
        </div>
        
        <button
          onClick={fetchReports}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Reports List */}
      {reports.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📄</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reports Found</h3>
          <p className="text-gray-600 mb-4">
            {filter === 'all' 
              ? 'No reports have been generated yet.' 
              : `No ${filter} reports found.`
            }
          </p>
          {showGenerateButton && onGenerateReport && (
            <button
              onClick={onGenerateReport}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Generate Your First Report
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {reports.map((report) => (
            <div
              key={report.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{getReportTypeIcon(report.report_type)}</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {report.title || `${report.report_type.charAt(0).toUpperCase() + report.report_type.slice(1)} Report`}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                          {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getReportTypeColor(report.report_type)}`}>
                          {report.report_type.charAt(0).toUpperCase() + report.report_type.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600 mb-3">
                    <p>Generated: {formatDate(report.generated_at)}</p>
                    {report.summary && (
                      <p className="mt-1 text-gray-700">{report.summary}</p>
                    )}
                  </div>

                  {report.content && report.content.overall_score && (
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-medium text-gray-600">Overall Score:</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full"
                            style={{ width: `${report.content.overall_score}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-semibold text-primary-600">
                          {Math.round(report.content.overall_score)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => setSelectedReportId(report.id)}
                    className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                    disabled={report.status !== 'completed'}
                  >
                    {report.status === 'completed' ? 'View' : 'Generating...'}
                  </button>
                  
                  {report.status === 'completed' && (
                    <>
                      <button
                        onClick={() => handleDownloadReport(report.id)}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-1"
                        title="Download Report"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download
                      </button>
                      
                      {/* Toggle for active/inactive - Only visible to admins */}
                      {isAdmin && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600">Active</span>
                          <button
                            onClick={() => handleToggleVisibility(report.id, report)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                              getReportVisibilityStatus(report) ? 'bg-green-600' : 'bg-gray-300'
                            }`}
                            title={
                              getReportVisibilityStatus(report)
                                ? 'Candidate can view this report' 
                                : 'Candidate cannot view this report'
                            }
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                getReportVisibilityStatus(report) ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <span className="text-xs text-gray-600">Inactive</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Report Viewer Modal */}
      {selectedReportId && (
        <ReportViewer
          reportId={selectedReportId}
          onClose={() => setSelectedReportId(null)}
        />
      )}
    </div>
  );
};

export default ReportList;



